import { Plus, HelpCircle, Info } from 'lucide-react';
import { cn } from '@/lib/utils';
import { resolveAvailability, type Availability, type AvailabilityTone } from '@/lib/nflAvailability';

/**
 * SAB-813: the two renderings of one designation. Both take the SAME resolved value from
 * `resolveAvailability`, so the row and the sheet cannot disagree about whether a player is
 * marked -- the failure that would be hardest to notice, because each surface looks
 * self-consistent on its own.
 *
 * Neither component decides anything. If you find yourself adding a value check here, it
 * belongs in nflAvailability.ts instead.
 */

/** Colour per tone, in one place so the pill and the strip stay the same language. */
const TONE: Record<AvailabilityTone, { pill: string; strip: string; icon: typeof Plus }> = {
  out: {
    pill: 'border-damage/40 bg-damage/[0.14] text-damage',
    strip: 'bg-damage/[0.16] text-damage border-b border-damage/30',
    icon: Plus,
  },
  caution: {
    pill: 'border-gold/40 bg-gold/[0.14] text-gold',
    strip: 'bg-gold/[0.16] text-gold border-b border-gold/30',
    icon: HelpCircle,
  },
  neutral: {
    pill: 'border-border bg-muted/60 text-muted-foreground',
    strip: 'bg-muted/60 text-muted-foreground border-b border-border',
    icon: Info,
  },
};

export interface AvailabilityProps {
  /** Weekly injury report value. Wins when set and not Active/Healthy. */
  injuryStatus?: string | null;
  /** Roster state. The fallback, and the only populated source before REG week 1. */
  status?: string | null;
  className?: string;
}

/**
 * Row pill. Compact by design: it sits in a draft-board row that already carries a position
 * pill, team, TRASH, ANTI, ADP and BYE.
 *
 * `data-compact` opts out of the global 44px tap-target floor at src/index.css:410. This is
 * a LABEL, not a control -- nothing happens when you tap it -- and without the opt-out the
 * mobile rule would inflate it to 44px square and blow the row height apart.
 */
export function AvailabilityPill({ injuryStatus, status, className }: AvailabilityProps) {
  const a = resolveAvailability(injuryStatus, status);
  if (!a) return null;
  const tone = TONE[a.tone];
  return (
    <span
      data-testid="availability-pill"
      data-availability={a.label}
      data-tone={a.tone}
      data-compact
      title={a.full}
      className={cn(
        'inline-flex flex-none items-center rounded border px-1 py-px',
        'font-display text-[9.5px] font-extrabold uppercase tracking-[0.04em] leading-[1.4]',
        // No truncate and no max-width: an unmapped value renders in full or not at all.
        // Clipping "Unknown" to "Unkn…" would be a quieter version of dropping it.
        'whitespace-nowrap',
        tone.pill,
        className,
      )}
    >
      {a.label}
    </span>
  );
}

/**
 * Sheet strip. Full-width band across the top of the player card, icon + label, matching the
 * reference: a red `+ IR` or a gold `? QUESTIONABLE` you cannot miss while scrolling.
 *
 * Uses `full`, not `label` -- the sheet has the width, and "QUESTIONABLE" reads better than
 * "Q" on a card you opened deliberately.
 */
export function AvailabilityStrip({ injuryStatus, status, className }: AvailabilityProps) {
  const a = resolveAvailability(injuryStatus, status);
  if (!a) return null;
  const tone = TONE[a.tone];
  const Icon = tone.icon;
  return (
    <div
      data-testid="availability-strip"
      data-availability={a.label}
      data-tone={a.tone}
      data-source={a.source}
      className={cn(
        // pr-12 reserves the top-right corner: the sheet's close button is positioned
        // absolutely against the MODAL, not the header, so it lands on this band. Without
        // the reserve a long unmapped designation would run underneath it.
        'flex w-full items-center gap-2 py-2 pl-5 pr-12',
        'font-display text-[12px] font-extrabold uppercase tracking-[0.08em]',
        tone.strip,
        className,
      )}
    >
      <Icon className="h-4 w-4 flex-none" aria-hidden />
      <span className="min-w-0 break-words">{a.full}</span>
    </div>
  );
}

export type { Availability };
